import { ReactNode } from 'react';
import cx from 'classix';

import { Icon, IconProps, Button } from '@platform/components';

export type AlertCTAProps = {
  text: string;
  callback: () => void;
  dismissOnSuccess?: boolean;
};

export type AlertProps = {
  variant: 'info' | 'warning' | 'error';
  children: ReactNode;
  onDismiss?: () => void;
  primaryCTA?: AlertCTAProps;
  secondaryCTA?: AlertCTAProps;
  additonalClassnames?: string;
};

export const Alert = ({
  variant,
  children,
  onDismiss,
  primaryCTA,
  secondaryCTA,
  additonalClassnames = '',
}: AlertProps) => {
  const iconType: IconProps['type'] =
    variant === 'info' ? 'info-circle'
    : variant === 'warning' ? 'alert-circle'
    : 'alert-triangle';

  return (
    <div
      className={cx(
        'border border-gray-200 shadow-xs w-[288px] md:w-full md:min-h-[72px] flex flex-col gap-3 md:flex-row md:items-center p-4 md:gap-2',
        'text-sm',
        variant === 'info' && 'bg-secondary-brand-25',
        variant === 'warning' && 'bg-warning-25',
        variant === 'error' && 'bg-error-25',
        additonalClassnames,
      )}
    >
      <div className='flex justify-between md:self-start'>
        <Icon type={iconType} />
        {onDismiss && (
          <Icon type='x-close' additionalClassNames='text-gray md:hidden' onClick={onDismiss} />
        )}
      </div>
      <div className='md:w-[60%]'>{children}</div>
      <div className='flex flex-col gap-3 md:flex-row md:gap-2 w-[50%] md:w-[30%] md:justify-center'>
        {primaryCTA && (
          <Button variant='secondary-gray' size='sm' onPress={primaryCTA.callback}>
            {primaryCTA.text}
          </Button>
        )}
        {secondaryCTA && (
          <Button variant='secondary-danger' size='sm' onPress={secondaryCTA.callback}>
            {secondaryCTA.text}
          </Button>
        )}
      </div>
      {onDismiss && (
        <Icon
          type='x-close'
          additionalClassNames='hidden md:block self-start ml-auto text-gray'
          onClick={onDismiss}
        />
      )}
    </div>
  );
};
